/**
 * content.ts
 * Products, Portfolio, News, and WhereToBuy cache management.
 */

import { Product } from '../products';
import { PortfolioProject } from '../portfolio-data';
import { PublicNewsItem, PublicWTBPoint } from '../dataSource';
import { cache, inFlight, isStale, notify } from './base';

const LS_WTB_KEY = 'sargo_where_to_buy';

// ─── Products ─────────────────────────────────────────────────────────────────

export function getCachedProducts(): Product[] | null {
  return cache.products?.data ?? null;
}

export async function fetchProducts(): Promise<Product[]> {
  if (cache.products && !isStale(cache.products)) {
    return cache.products.data;
  }
  if (inFlight.products) return inFlight.products;

  const { getProductsFromDb } = await import('../dataSource');
  inFlight.products = getProductsFromDb().then((data) => {
    cache.products = { data, fetchedAt: Date.now() };
    inFlight.products = null;
    notify();
    return data;
  }).catch(() => {
    inFlight.products = null;
    return cache.products?.data ?? [];
  });

  return inFlight.products;
}

export function invalidateProductsCache() {
  cache.products = null;
  inFlight.products = null;
}

// ─── Portfolio ────────────────────────────────────────────────────────────────

export function getCachedPortfolio(): PortfolioProject[] | null {
  return cache.portfolio?.data ?? null;
}

export async function fetchPortfolio(): Promise<PortfolioProject[]> {
  if (cache.portfolio && !isStale(cache.portfolio)) {
    return cache.portfolio.data;
  }
  if (inFlight.portfolio) return inFlight.portfolio;

  const { getPortfolioFromDb } = await import('../dataSource');
  inFlight.portfolio = getPortfolioFromDb().then((data) => {
    const sorted = [...data].sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    });
    cache.portfolio = { data: sorted, fetchedAt: Date.now() };
    inFlight.portfolio = null;
    notify();
    return sorted;
  }).catch(() => {
    inFlight.portfolio = null;
    return cache.portfolio?.data ?? [];
  });

  return inFlight.portfolio;
}

export function invalidatePortfolioCache() {
  cache.portfolio = null;
  inFlight.portfolio = null;
}

// ─── News ─────────────────────────────────────────────────────────────────────

export function getCachedNews(): PublicNewsItem[] | null {
  return cache.news?.data ?? null;
}

export async function fetchNews(): Promise<PublicNewsItem[]> {
  if (cache.news && !isStale(cache.news)) {
    return cache.news.data;
  }
  if (inFlight.news) return inFlight.news;

  const { getNewsFromDb } = await import('../dataSource');
  inFlight.news = getNewsFromDb().then((data) => {
    cache.news = { data, fetchedAt: Date.now() };
    inFlight.news = null;
    notify();
    return data;
  }).catch(() => {
    inFlight.news = null;
    return cache.news?.data ?? [];
  });

  return inFlight.news;
}

export function invalidateNewsCache() {
  cache.news = null;
  inFlight.news = null;
}

// ─── WhereToBuy ───────────────────────────────────────────────────────────────

export function getCachedWhereToBuy(): PublicWTBPoint[] | null {
  return cache.whereToBuy?.data ?? null;
}

export async function fetchWhereToBuy(): Promise<PublicWTBPoint[]> {
  if (cache.whereToBuy && !isStale(cache.whereToBuy)) {
    return cache.whereToBuy.data;
  }
  if (inFlight.whereToBuy) return inFlight.whereToBuy;

  const { getWhereToBuyFromDb } = await import('../dataSource');
  inFlight.whereToBuy = getWhereToBuyFromDb().then((data) => {
    const fetchedAt = Date.now();
    cache.whereToBuy = { data, fetchedAt };
    inFlight.whereToBuy = null;
    try {
      localStorage.setItem(LS_WTB_KEY, JSON.stringify({ data, fetchedAt }));
    } catch { /* ignore */ }
    notify();
    return data;
  }).catch(() => {
    inFlight.whereToBuy = null;
    return cache.whereToBuy?.data ?? [];
  });

  return inFlight.whereToBuy;
}

export function invalidateWhereToBuyCache() {
  cache.whereToBuy = null;
  inFlight.whereToBuy = null;
  try { localStorage.removeItem(LS_WTB_KEY); } catch { /* ignore */ }
}

export function invalidateWhereToBuyStorageCache() {
  try { localStorage.removeItem(LS_WTB_KEY); } catch { /* ignore */ }
}

// ─── Initialization ──────────────────────────────────────────────────────────

// (function preloadWhereToBuyFromStorage() {
//   try {
//     const raw = localStorage.getItem(LS_WTB_KEY);
//     if (!raw) return;
//     const parsed = JSON.parse(raw);
//     if (parsed && typeof parsed === 'object' && 'fetchedAt' in parsed && 'data' in parsed) {
//       cache.whereToBuy = { data: parsed.data, fetchedAt: parsed.fetchedAt };
//     }
//   } catch { /* ignore */ }
// })();
